import { LocalStorage } from 'Roblox';
import { DISMISS_TEMPORARY_TTL_SECONDS, FEATURE_NAME, LOG_PREFIX } from '../app.config';
import { PromptType, ViewType } from '../constants/parameters';

enum DismissType {
  TEMPORARY = 'TEMPORARY',
  FOREVER = 'FOREVER'
}

/**
 * A class encapsulating storage of user preferences (e.g. how long to dismiss
 * a prompt for) based on the Local Storage API.
 */
export class PreferenceServiceDefault {
  /**
   * Increment this when backwards-incompatible changes are made.
   */
  private static readonly VERSION_NUMBER = 1;

  private static readonly KEY_PREFIX = `${FEATURE_NAME}.${PreferenceServiceDefault.VERSION_NUMBER}`;

  /**
   * Clears preferences from local storage.
   */
  static clear(): void {
    if (!LocalStorage.isAvailable()) {
      return;
    }

    // Delete all keys for this feature (and the current version number).
    const keys = Object.keys(localStorage).filter(key =>
      key.startsWith(`${PreferenceServiceDefault.KEY_PREFIX}.`)
    );
    keys.forEach(key => localStorage.removeItem(key));

    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, 'Clearing local preferences');
  }

  private static getPrefixedKey = (suffix: string) =>
    `${PreferenceServiceDefault.KEY_PREFIX}.${suffix}`;

  private storageKeyDismissedAt: string;

  private storageKeyDismissType: string;

  private localStorageAvailable: boolean;

  private dismissedAtLastValue: string | null | undefined;

  constructor(userId: string, promptType: PromptType, viewType: ViewType) {
    this.storageKeyDismissedAt = PreferenceServiceDefault.getPrefixedKey(
      `${userId}.${promptType}.${viewType}.dismissedAt`
    );
    this.storageKeyDismissType = PreferenceServiceDefault.getPrefixedKey(
      `${userId}.${promptType}.${viewType}.dismissType`
    );
    this.localStorageAvailable = LocalStorage.isAvailable();
  }

  isPromptDismissed(): boolean {
    if (!this.localStorageAvailable) {
      return false;
    }

    const dismissedAt = localStorage.getItem(this.storageKeyDismissedAt);
    const dismissType = localStorage.getItem(this.storageKeyDismissType);

    if (this.dismissedAtLastValue === undefined || dismissedAt !== this.dismissedAtLastValue) {
      // eslint-disable-next-line no-console, @typescript-eslint/restrict-template-expressions
      console.log(LOG_PREFIX, `Found dismiss type [${dismissType}] at [${dismissedAt}]`);
      this.dismissedAtLastValue = dismissedAt;
    }

    // Case: No value for keys.
    if (dismissType === null || dismissedAt === null) {
      return false;
    }

    // Case: Prompt dismissed forever.
    if (dismissType === DismissType.FOREVER) {
      return true;
    }

    // Case: Prompt dismissed for a day.
    const dismissedAtTime = parseInt(dismissedAt, 10);
    if (
      !Number.isNaN(dismissedAtTime) &&
      Date.now() < dismissedAtTime + DISMISS_TEMPORARY_TTL_SECONDS * 1000
    ) {
      return true;
    }

    // Case: Junk value for key.
    return false;
  }

  /**
   * Saves a dismiss preference for the account security prompt.
   */
  dismissPrompt(isForever: boolean): void {
    if (!this.localStorageAvailable) {
      return;
    }

    const dismissType = isForever ? DismissType.FOREVER : DismissType.TEMPORARY;
    const dismissedAt = Date.now().toString();
    localStorage.setItem(this.storageKeyDismissType, dismissType);
    localStorage.setItem(this.storageKeyDismissedAt, dismissedAt);

    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, `Saved dismiss type [${dismissType}] at [${dismissedAt}]`);
  }
}

/**
 * An interface encapsulating storage of user preferences (e.g. how long to
 * dismiss a prompt for) based on the Local Storage API.
 *
 * This interface type offers future flexibility e.g. for mocking the default
 * preference service.
 */
export type PreferenceService = {
  [K in keyof PreferenceServiceDefault]: PreferenceServiceDefault[K];
};
